const axios = require('axios');
const forge = require('node-forge');
const jwt = require('jsonwebtoken');

const uuid = require('uuid');

const { Binary } = require('binary');

const base64 = require('virtru-base64');
const EventEmitter = require('events');
const Url = require('url-parse');
const crc32 = require('buffer-crc32');
const RemoteTdfNav = require('./utils/remote-tdf-nav');
const TdfNavigator = require('./utils/tdf-navigator');
const { base64ToBuffer, keysplit: keysplitUtil } = require('./utils');

const CryptoService = require('./crypto/crypto-service');

// configurable
// TODO: remove dependencies from ciphers so that we can open-source instead of relying on other Virtru libs
const ciphers = require('./ciphers');

const EncryptionInformation = require('./models/encryption-information');
const AttributeSet = require('./models/attribute-set');
const KeyAccess = require('./models/key-access');

const MockStream = require('./utils/mock-stream.js');

const htmlWrapperTemplate = require('./templates/default.html');
const HttpRequest = require('./client/auth/http-request');

const {
  KasDecryptError,
  KasUpsertError,
  KeyAccessError,
  KeySyncError,
  ManifestIntegrityError,
  PolicyIntegrityError,
  TdfDecryptError,
  TdfPayloadExtractionError,
} = require('./errors');

// TODO: input validation on manifest JSON
const DEFAULT_SEGMENT_SIZE = 1024 * 1024;

// TDF3
class TDF extends EventEmitter {
  constructor() {
    super();
    // default
    this.config = {};
    this.policy = {};

    this.mimeType = null;
    this.content = null;
    this.contentStream = null;

    this.manifest = null;
    this.payload = null;

    this.encryptionInformation = null;

    this.attributeSet = new AttributeSet();

    this.publicKey = '';
    this.privateKey = '';

    this.integrityAlgorithm = 'HS256';
    this.htmlTransferUrl = null;

    this.segmentIntegrityAlgorithm = this.integrityAlgorithm;

    this.segmentSizeDefault = DEFAULT_SEGMENT_SIZE;
  }

  // factory
  static create() {
    return new TDF();
  }

  static createCipher(type) {
    return ciphers[type].create(CryptoService);
  }

  static async generateKeyPair() {
    const keyPair = await CryptoService.generateKeyPair();
    return keyPair;
  }

  static async generatePolicyUuid() {
    return uuid.v4();
  }

  /**
   *
   * @param {Buffer} payload - This is the payload. It must be a buffer.
   * @param {json} manifest - This is the manifest
   * @param {String} transferUrl
   */
  static wrapHtml(payload, manifest, transferUrl) {
    const parsedUrl = new Url(transferUrl);
    const { origin } = parsedUrl;
    const exportManifest = typeof manifest === 'string' ? manifest : JSON.stringify(manifest);

    const fullHtmlString = htmlWrapperTemplate({
      transferUrl,
      transferBaseUrl: origin,
      manifest: base64.encode(exportManifest),
      payload: payload.toString('base64'),
    });

    return Binary.fromString(fullHtmlString).asBuffer();
  }

  static unwrapHtml(htmlPayload) {
    const html = htmlPayload.toString();
    const payloadRe = /<input id=['"]?data-input['"]?[^>]*value=['"]?([a-zA-Z0-9+/=]+)['"]?/;
    try {
      const base64Payload = payloadRe.exec(html)[1];
      return base64ToBuffer(base64Payload);
    } catch (e) {
      throw new TdfPayloadExtractionError('There was a problem extracting the TDF3 payload', e);
    }
  }

  static extractPemFromKeyString(keyString) {
    let pem = keyString;

    // Skip the public key extraction if we find that the KAS url provides a
    // PEM-encoded key instead of certificate
    if (keyString.includes('CERTIFICATE')) {
      const cert = forge.pki.certificateFromPem(keyString);
      pem = forge.pki.publicKeyToPem(cert.publicKey);
    }

    return pem;
  }

  static createMockStream(streamContent, isReadStream) {
    return new MockStream(streamContent, isReadStream);
  }

  // Extracts the TDF's manifest
  static async getManifestFromRemoteTDF(url, customHeaders = {}) {
    const zipHelper = new TdfNavigator(
      {
        type: 'remote',
        location: url,
      },
      customHeaders
    );

    const centralDirectory = await zipHelper.getCentralDirectory();
    return await zipHelper.getManifest(centralDirectory, '0.manifest.json');
  }

  // Extracts the TDF's manifest and thus the policy from a remote TDF
  // DEPRECATED
  static async getPolicyFromRemoteTDF(url, customHeaders = {}) {
    const manifest = this.getManifestFromRemoteTDF(url, customHeaders);
    return base64.decode(manifest.encryptionInformation.policy);
  }

  static stringToBinary(content) {
    return Binary.fromString(content);
  }

  setProtocol() {
    console.error('protocol is ignored; use client.encrypt instead');
    return this;
  }

  setHtmlTransferUrl(url) {
    this.htmlTransferUrl = url;
    return this;
  }

  // customRequestBuilder is any function that can be used to build a custom request body and headers
  // The builder must accept an object of the following (ob.body, ob.headers, ob.method, ob.url)
  // and return an object with body and headers params
  setCustomRequestBuilder(customRequestBuilder) {
    this.customRequestBuilder = customRequestBuilder;
    return this;
  }

  // AuthProvider is a class that can be used to build a custom request body and headers
  // The builder must accept an object of the following (ob.body, ob.headers, ob.method, ob.url)
  // and mutate it in place.
  setAuthProvider(authProvider) {
    this.authProvider = authProvider;
    return this;
  }

  setEncryption(opts) {
    switch (opts.type) {
      case 'split':
      default:
        this.encryptionInformation = new EncryptionInformation.SplitKey(
          TDF.createCipher(opts.cipher || 'aes-256-gcm')
        );
        break;
    }
    return this;
  }

  /**
   * Build a key access object and add it to the list. Can specify either
   * a (url, publicKey) pair (legacy, deprecated) or an attribute URL (future).
   * If all are missing then it attempts to use the default attribute. If that
   * is missing it throws an error.
   * @param  {Object} options
   * @param  {String} options.type - enum representing how the object key is treated
   * @param  {String} [ options.attributeUrl ] - URL of the attribute to use for pubKey and kasUrl. Omit to use default.
   * @param  {String} [ options.url ] - @deprecated directly set the KAS URL
   * @param  {String} [ options.publicKey ] - @deprecated directly set the (KAS) public key
   * @param  {String? Object?} [ options.metadata ] - Metadata. Appears to be dead code.
   * @return {<TDF>}- this instance
   */
  addKeyAccess({ type, url, publicKey, attributeUrl, metadata = '' }) {
    // TODO - run down metadata parameter. Clean it out if it isn't used this way anymore.

    /** Internal function to keep it DRY */
    function createKeyAccess(type, kasUrl, pubKey, metadata) {
      const keyAccess = null;
      switch (type) {
        case 'wrapped':
          return new KeyAccess.Wrapped(kasUrl, pubKey, metadata);
        case 'remote':
          return new KeyAccess.Remote(kasUrl, pubKey, metadata);
        default:
          throw new KeyAccessError(`TDF.addKeyAccess: Key access type ${type} is unknown`);
      }
    }

    /** Another internal function to keep it dry */
    function loadKeyAccess(encryptionInformation, keyAccess) {
      if (!encryptionInformation) {
        throw new KeyAccessError('TDF.addKeyAccess: Encryption Information not set');
      }
      encryptionInformation.keyAccess.push(keyAccess);
    }

    // If an attributeUrl is provided try to load with that first.
    if (attributeUrl) {
      const attr = this.attributeSet.get(attributeUrl);
      if (attr) {
        loadKeyAccess(
          this.encryptionInformation,
          createKeyAccess(type, attr.kasUrl, attr.pubKey, metadata)
        );
        return this;
      }
    }

    // if url and pulicKey are specified load the key access object with them
    if (url && publicKey) {
      loadKeyAccess(
        this.encryptionInformation,
        createKeyAccess(type, url, TDF.extractPemFromKeyString(publicKey), metadata)
      );
      return this;
    }

    // Assume the default attribute is the source for kasUrl and pubKey
    const defaultAttr = this.attributeSet.getDefault();
    if (defaultAttr) {
      const { pubKey, kasUrl } = defaultAttr;
      loadKeyAccess(
        this.encryptionInformation,
        createKeyAccess(type, kasUrl, TDF.extractPemFromKeyString(pubKey), metadata)
      );
      return this;
    }
    // All failed. Raise an error.
    throw new KeyAccessError('TDF.addKeyAccess: No source for kasUrl or pubKey');
  }

  setPolicy(policy) {
    this.validatePolicyObject(policy);
    this.policy = policy;
    return this;
  }

  /**
   * Add an entity object. This contains attributes with public key info that
   * is used to make splits and wrap object keys.
   * @param  {Object} entity - EntityObject
   * @return {<TDF>}- this instance
   */
  setEntity(entity) {
    this.entity = entity;
    // Harvest the attributes from this entity object
    // Don't wait for this promise to resolve.
    this.attributeSet.addJwtAttributes(this.entity.attributes);
    return this;
  }

  setPublicKey(publicKey) {
    this.publicKey = publicKey;
    return this;
  }

  setPrivateKey(privateKey) {
    this.privateKey = privateKey;
    return this;
  }

  setDefaultSegmentSize(segmentSizeDefault) {
    this.segmentSizeDefault = segmentSizeDefault;
    return this;
  }

  setIntegrityAlgorithm(integrityAlgorithm, segmentIntegrityAlgorithm) {
    this.integrityAlgorithm = integrityAlgorithm.toUpperCase();
    this.segmentIntegrityAlgorithm = (
      segmentIntegrityAlgorithm || integrityAlgorithm
    ).toUpperCase();
    return this;
  }

  // this must be binary!
  addContent(content, mimeType) {
    this.content = content;
    this.mimeType = mimeType;
    return this;
  }

  addContentStream(contentStream, mimeType) {
    this.contentStream = contentStream;
    this.mimeType = mimeType;
    return this;
  }

  validatePolicyObject(policy) {
    const missingFields = [];

    if (!policy.uuid) missingFields.push('uuid');
    if (!policy.body) missingFields.push('body', 'body.dissem');
    if (policy.body && !policy.body.dissem) missingFields.push('body.dissem');

    if (missingFields.length) {
      throw new PolicyIntegrityError(
        `The given policy object requires the following properties: ${missingFields}`
      );
    }
  }

  async _generateManifest(keyInfo) {
    // (maybe) Fields are quoted to avoid renaming
    const payload = {
      type: 'reference',
      url: '0.payload',
      protocol: 'zip',
      isEncrypted: true,
      schemaVersion: '3.0.0',
      ...(this.mimeType && { mimeType: this.mimeType }),
    };
    const encryptionInformationStr = await this.encryptionInformation.write(this.policy, keyInfo);

    return {
      payload,
      // generate the manifest first, then insert integrity information into it
      encryptionInformation: encryptionInformationStr,
    };
  }

  async getSignature(unwrappedKeyBinary, payloadBinary, algorithmType) {
    switch (algorithmType.toLowerCase()) {
      case 'hs256':
      default:
        // simple hmac is the default
        const payloadSigStr = await CryptoService.hmac(
          unwrappedKeyBinary.asBuffer().toString('hex'),
          payloadBinary.asBuffer()
        );
        return payloadSigStr;
        break;
      case 'gmac':
        // use the auth tag baked into the encrypted payload
        return payloadBinary.asBuffer().slice(-16).toString('hex');
        break;
    }
  }

  // Allows explicit key syncing using an already-loaded manifest
  async sync() {
    if (this.manifest) {
      await this.upsert(this.manifest, true);
    } else {
      throw new KeySyncError(
        'Key syncing requires a loaded TDF manifest. Please use "loadTDFStream" first to load a manifest.'
      );
    }
  }

  // Provide an upsert of key information via each KAS
  // ignoreType if true skips the key access type check when syncing
  async upsert(unsavedManifest, ignoreType = false) {
    const { keyAccess, policy } = unsavedManifest.encryptionInformation;
    const { entity } = this;
    return Promise.all(
      keyAccess.map(async (keyAccessObject) => {
        // We only care about remote key access objects for the policy sync portion
        const isRemote = KeyAccess.KeyUtil.isRemote(keyAccessObject);
        if (!ignoreType && !isRemote) {
          return;
        }

        const url = `${keyAccessObject.url}/upsert`;

        const httpReq = new HttpRequest();
        httpReq.body = {
          keyAccess: keyAccessObject,
          entity,
          policy,
          // TODO: We can optimize this function by only signing a JWT once
          authToken: jwt.sign({}, this.privateKey, { algorithm: 'RS256', expiresIn: 60 }),
        };
        httpReq.url = url;
        httpReq.method = 'post';

        if (this.authProvider) {
          await this.authProvider.injectAuth(httpReq);
        }

        try {
          await axios.post(httpReq.url, httpReq.body, { headers: httpReq.headers });

          // Remove additional properties which were needed to sync, but not that we want to save to
          // the manifest
          delete keyAccessObject.wrappedKey;
          delete keyAccessObject.encryptedMetadata;
          delete keyAccessObject.policyBinding;

          if (isRemote) {
            // Decode the policy and extract only the required info to save -- the uuid
            const decodedPolicy = JSON.parse(base64.decode(policy));
            const newEncodedPolicy = base64.encode(JSON.stringify({ uuid: decodedPolicy.uuid }));
            unsavedManifest.encryptionInformation.policy = newEncodedPolicy;
          }
        } catch (e) {
          throw new KasUpsertError('Unable to perform upsert operation on the KAS', e);
        }
      })
    );
  }

  async writeStream(outputStream, byteLimit) {
    const self = this;
    const segmentInfos = [];
    if (!byteLimit) {
      byteLimit = Number.MAX_SAFE_INTEGER;
    }

    const entryInfos = [
      {
        filename: '0.payload',
      },
      {
        filename: '0.manifest.json',
      },
    ];

    let currentBuffer = new Buffer(0);

    let totalByteCount = 0;
    let crcCounter = 0;
    let fileByteCount = 0;
    let aggregateHash = '';

    const zipHelper = new RemoteTdfNav('', {});
    const keyInfo = await this.encryptionInformation.generateKey();

    this.manifest = await this._generateManifest(keyInfo);

    // For all remote key access objects, sync its policy
    await this.upsert(this.manifest);

    // determine default segment size by writing empty buffer
    const { segmentSizeDefault } = this;
    const encryptedBlargh = await this.encryptionInformation.encrypt(
      Binary.fromBuffer(new Buffer(segmentSizeDefault)),
      keyInfo.unwrappedKeyBinary
    );
    const payloadBuffer = encryptedBlargh.payload.asBuffer();
    const encryptedSegmentSizeDefault = payloadBuffer.length;

    // start writing the content
    entryInfos[0].filename = '0.payload';
    entryInfos[0].offset = totalByteCount;

    await _writeHeader(entryInfos[0].filename);
    crcCounter = 0;
    fileByteCount = 0;

    // TODO: we are ensuring these get executed in order and completely.. unsure of how this will affect backpressure
    let writingDataPromise = Promise.resolve();

    this.contentStream.on('data', (str) => {
      writingDataPromise = writingDataPromise.then(
        () =>
          new Promise(async (resolve, reject) => {
            currentBuffer = Buffer.concat([currentBuffer, Buffer.from(str)]);
            while (currentBuffer.length > segmentSizeDefault) {
              const segment = currentBuffer.slice(0, segmentSizeDefault);
              await _encryptAndWriteSegment(segment);

              currentBuffer = currentBuffer.slice(segmentSizeDefault);
            }
            resolve();
          })
      );
    });

    this.contentStream.on('end', async () => {
      // finish up pending segments
      await writingDataPromise;

      if (currentBuffer.length > 0) {
        await _encryptAndWriteSegment(currentBuffer);
        currentBuffer = null;
      }

      entryInfos[0].crcCounter = crcCounter;
      entryInfos[0].fileByteCount = fileByteCount;
      await _writeChunk(zipHelper.writeDataDescriptor(crcCounter, fileByteCount));

      // prepare the manifest
      entryInfos[1].filename = '0.manifest.json';
      entryInfos[1].offset = totalByteCount;
      await _writeHeader(entryInfos[1].filename);
      crcCounter = 0;
      fileByteCount = 0;

      const { manifest } = this;

      // hash the concat of all hashes
      const payloadSigStr = await this.getSignature(
        keyInfo.unwrappedKeyBinary,
        Binary.fromString(aggregateHash),
        this.integrityAlgorithm
      );
      manifest.encryptionInformation.integrityInformation.rootSignature.sig = base64.encode(
        payloadSigStr
      );
      manifest.encryptionInformation.integrityInformation.rootSignature.alg = this.integrityAlgorithm;

      manifest.encryptionInformation.integrityInformation.segmentSizeDefault = segmentSizeDefault;
      manifest.encryptionInformation.integrityInformation.encryptedSegmentSizeDefault = encryptedSegmentSizeDefault;
      manifest.encryptionInformation.integrityInformation.segmentHashAlg = this.segmentIntegrityAlgorithm;
      manifest.encryptionInformation.integrityInformation.segments = segmentInfos;

      manifest.encryptionInformation.method.isStreamable = true;

      // write the manifest
      const manifestBuffer = Buffer.from(JSON.stringify(manifest));
      await _writeChunk(manifestBuffer);
      entryInfos[1].crcCounter = crcCounter;
      entryInfos[1].fileByteCount = fileByteCount;
      await _writeChunk(zipHelper.writeDataDescriptor(crcCounter, fileByteCount));

      // write the central directory out
      const centralDirectoryByteCount = totalByteCount;
      for (let i = 0; i < entryInfos.length; i++) {
        const entryInfo = entryInfos[i];
        const uncompressedSize = fileByteCount;
        await _writeChunk(
          zipHelper.writeCentralDirectoryRecord(
            entryInfo.fileByteCount,
            entryInfo.filename,
            entryInfo.offset,
            entryInfo.crcCounter,
            2175008768
          )
        );
      }
      const endOfCentralDirectoryByteCount = totalByteCount - centralDirectoryByteCount;
      await _writeChunk(
        zipHelper.writeEndOfCentralDirectoryRecord(
          entryInfos.length,
          endOfCentralDirectoryByteCount,
          centralDirectoryByteCount
        )
      );

      // seal the deal
      outputStream.end();
    });

    return outputStream;

    // nested helper fn's
    function _writeHeader(filename) {
      return _writeChunk(zipHelper.getLocalFileHeader(filename, 0, 0, 0));
    }

    function _writeChunk(chunk) {
      return new Promise((resolve, reject) => {
        let ok = false;
        ok = outputStream.write(chunk, function () {
          totalByteCount += chunk.length;
          if (totalByteCount > byteLimit) {
            reject(new Error(`Safe byte limit (${byteLimit}) exceeded`));
          }
          crcCounter = crc32.unsigned(chunk, crcCounter);
          fileByteCount += chunk.length;
          // if we can keep writing, exit and get ready for the next chunk
          if (ok) {
            resolve();
          }
        });
        if (!ok) {
          // if we can't keep writing, wait to resolve until we get the drain event
          // see: https://nodejs.org/api/stream.html#stream_writable_write_chunk_encoding_callback
          outputStream.once('drain', resolve);
        }
      });
    }

    async function _encryptAndWriteSegment(chunk) {
      // Don't pass in an IV here. The encrypt function will generate one for you, ensuring that each segment has a unique IV.
      const encryptedResult = await self.encryptionInformation.encrypt(
        Binary.fromBuffer(chunk),
        keyInfo.unwrappedKeyBinary
      );
      const payloadBuffer = encryptedResult.payload.asBuffer();
      const payloadSigStr = await self.getSignature(
        keyInfo.unwrappedKeyBinary,
        encryptedResult.payload,
        self.segmentIntegrityAlgorithm
      );

      // combined string of all hashes for root signature
      aggregateHash += payloadSigStr;

      segmentInfos.push({
        hash: base64.encode(payloadSigStr),
        segmentSize: chunk.length === segmentSizeDefault ? undefined : chunk.length,
        encryptedSegmentSize:
          payloadBuffer.length === encryptedSegmentSizeDefault ? undefined : payloadBuffer.length,
      });

      return _writeChunk(encryptedResult.payload.asBuffer());
    }
  }

  // load the TDF as a stream in memory, for further use in reading and key syncing
  async loadTDFStream(dataSource, customHeaders = {}) {
    const zipHelper = new TdfNavigator(dataSource);
    const centralDirectory = await zipHelper.getCentralDirectory();

    this.manifest = await zipHelper.getManifest(centralDirectory, '0.manifest.json');
    return { zipHelper, centralDirectory };
  }

  async unwrapKey(manifest) {
    // Create an auth token so the KAS knows we actually have a private key
    // Expires in 60 seconds
    const authToken = jwt.sign({}, this.privateKey, { algorithm: 'RS256', expiresIn: 60 });
    const { keyAccess } = manifest.encryptionInformation;
    let responseMetadata;

    // Get key access information to know the KAS URLS
    // TODO: logic that runs on multiple KAS's
    const rewrappedKeys = await Promise.all(
      keyAccess.map(async (keySplitInfo) => {
        const url = `${keySplitInfo.url}/rewrap`;

        const httpReq = new HttpRequest();
        httpReq.body = {
          keyAccess: keySplitInfo,
          entity: {
            ...this.entity,
            publicKey: this.publicKey,
          },
          policy: manifest.encryptionInformation.policy,
          authToken,
        };
        httpReq.url = url;
        httpReq.method = 'post';

        if (this.authProvider) {
          await this.authProvider.injectAuth(httpReq);
        }

        try {
          // The response from KAS on a rewrap
          const {
            data: { entityWrappedKey, metadata },
          } = await axios.post(httpReq.url, httpReq.body, { headers: httpReq.headers });
          responseMetadata = metadata;
          const key = Binary.fromString(base64.decode(entityWrappedKey));
          const decryptedKeyBinary = await CryptoService.decryptWithPrivateKey(
            key,
            this.privateKey
          );
          this.emit('rewrap', metadata);
          return decryptedKeyBinary.asBuffer();
        } catch (e) {
          throw new KasDecryptError('Unable to decrypt the response from KAS', e);
        }
      })
    );

    // Merge the unwrapped keys from each KAS
    const reconstructedKey = keysplitUtil.keyMerge(rewrappedKeys);
    const reconstructedKeyBinary = Binary.fromArrayBuffer(reconstructedKey);

    return {
      reconstructedKeyBinary,
      metadata: responseMetadata,
    };
  }

  /**
   * readStream
   *
   * @param {Object} dataSource - A data source object describing where the data resides, and how the function should fetch it
   * -- An example 'remote' type
   * {
   *   type: 'remote',
   *   location: 'https://somefileurl.com/somefile.zip'
   * }
   * @param {*} outputStream - The writable stream we should put the new bits into
   * @param {*} customHeaders - Optional field to specify any custom headers that should be added to the request for 'remote' types
   */
  async readStream(dataSource, outputStream, customHeaders = {}) {
    const { zipHelper, centralDirectory } = await this.loadTDFStream(dataSource, customHeaders);

    outputStream.manifest = this.manifest;
    if (outputStream.emit) {
      outputStream.emit('manifest', this.manifest);
    }

    const { segments } = this.manifest.encryptionInformation.integrityInformation;
    const { reconstructedKeyBinary, metadata } = await this.unwrapKey(this.manifest);

    outputStream.metadata = metadata;

    // If the output stream can emit events, then emit the rewrap response.
    if (outputStream.emit) {
      outputStream.emit('rewrap', metadata);
    }

    // check the combined string of hashes
    const integrityAlgorithmType = this.manifest.encryptionInformation.integrityInformation
      .rootSignature.alg;
    const payloadSigStr = await this.getSignature(
      reconstructedKeyBinary,
      Binary.fromString(segments.map((segment) => base64.decode(segment.hash)).join('')),
      integrityAlgorithmType
    );

    if (
      this.manifest.encryptionInformation.integrityInformation.rootSignature.sig !==
      base64.encode(payloadSigStr)
    ) {
      throw new ManifestIntegrityError('Failed integrity check on root signature');
    }

    // use the wrapped key to decrypt the payload
    const cipher = TDF.createCipher(
      this.manifest.encryptionInformation.method.algorithm.toLowerCase()
    );

    const encryptedSegmentSizeDefault =
      parseInt(
        this.manifest.encryptionInformation.integrityInformation.encryptedSegmentSizeDefault
      ) || DEFAULT_SEGMENT_SIZE;

    // TODO: Don't await on each segment serially, instead use event-driven approach to prevent deadlock.
    // See: https://github.com/jherwitz/tdf3-js/blob/3ec3c8a3b8c5cecb6f6976b540d5ecde21183c8c/src/tdf.js#L739
    let encryptedOffset = 0;
    for (let i = 0; i < segments.length; i++) {
      const encryptedSegmentSize = segments[i].encryptedSegmentSize || encryptedSegmentSizeDefault;
      const encryptedChunk = await zipHelper.getPayloadSegment(
        centralDirectory,
        '0.payload',
        encryptedOffset,
        encryptedSegmentSize
      );
      encryptedOffset += encryptedSegmentSize;

      // use the segment alg type if provided, otherwise use the root sig alg
      const segmentIntegrityAlgorithmType = this.manifest.encryptionInformation.integrityInformation
        .segmentHashAlg;
      const segmentHashStr = await this.getSignature(
        reconstructedKeyBinary,
        Binary.fromBuffer(encryptedChunk),
        segmentIntegrityAlgorithmType || integrityAlgorithmType
      );

      if (segments[i].hash !== base64.encode(segmentHashStr)) {
        throw new ManifestIntegrityError('Failed integrity check on segment hash');
      }

      let decryptedSegment;

      try {
        decryptedSegment = await cipher.decrypt(encryptedChunk, reconstructedKeyBinary);
      } catch (e) {
        throw new TdfDecryptError(
          'Error decrypting payload. This suggests the key used to decrypt the payload is not correct.',
          e
        );
      }

      await _writeChunk(decryptedSegment.payload.asBuffer());
    }

    // TODO: wait for finish event?
    outputStream.end();

    return Promise.resolve();

    function _writeChunk(chunk) {
      return new Promise((resolve, reject) => {
        outputStream.write(chunk);
        // Don't wait on flush or res code. This will cause the stream to be greedily filled
        // (i.e., effectively writing the entire data to a buffer). But since we already don't
        // support streaming for decrypt it's not a significant impediment.
        resolve();
      });
    }
  }
}

if (typeof window !== 'undefined') {
  window.TDF = TDF;
}

module.exports = TDF;
